import { useEffect, useMemo, useState } from 'react';
import { Search, Info } from 'lucide-react';
import useAuthStore from '../store/authStore';
import useMetricsStore from '../store/metricsStore';
import { hasCapability } from '../utils/permissions';
import PeriodPicker, { defaultPeriod } from '../components/metrics/PeriodPicker';
import { formatDuration } from '../utils/metricsFormat';
import { formatAbsolute } from '../utils/formatRelative';

function initials(name) {
  return name ? name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '?';
}

function Avatar({ name, url, size = 26 }) {
  return (
    <div
      className="rounded-full bg-accent/20 text-accent flex items-center justify-center font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {url ? <img src={url} alt={name} className="w-full h-full rounded-full object-cover" /> : initials(name)}
    </div>
  );
}

// "Uso e atividade" (M6b) — GET /api/v1/metrics/people (M6a). Rótulo
// deliberadamente NUNCA "produtividade" (pedido do marco): iniciar/concluir
// itens e tempo em espera não julgam desempenho, só descrevem uso.
//
// Quem não tem a capability org.metrics.people só vê as próprias métricas
// (o backend permite isso sem capability — "as próprias métricas nunca
// dependem de capability", metrics.routes.js) — por isso o fetch aqui SEMPRE
// manda user_id=self nesse caso, nunca omite (omitir pediria a lista geral,
// que o backend rejeitaria com 403).
export default function PeopleMetricsPage() {
  const user = useAuthStore((s) => s.user);
  const canSeeAll = hasCapability(user, 'org.metrics.people');
  const peopleMetrics = useMetricsStore((s) => s.peopleMetrics);
  const peopleMetricsMeta = useMetricsStore((s) => s.peopleMetricsMeta);
  const loadingPeopleMetrics = useMetricsStore((s) => s.loadingPeopleMetrics);
  const peopleMetricsError = useMetricsStore((s) => s.peopleMetricsError);
  const fetchPeopleMetrics = useMetricsStore((s) => s.fetchPeopleMetrics);
  const [period, setPeriod] = useState(() => defaultPeriod(30));
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    fetchPeopleMetrics({
      from: period.from.toISOString(),
      to: period.to.toISOString(),
      userId: canSeeAll ? undefined : user.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, canSeeAll, user?.id]);

  const filtered = useMemo(() => {
    if (!canSeeAll || !search.trim()) return peopleMetrics;
    const q = search.trim().toLowerCase();
    return peopleMetrics.filter((p) => p.name?.toLowerCase().includes(q));
  }, [peopleMetrics, search, canSeeAll]);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-5 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">Uso e atividade</h1>
          <p className="text-sm text-text-secondary mt-1 flex items-start gap-1.5 max-w-2xl">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              Quantos itens cada pessoa iniciou e concluiu, e quanto tempo passou em espera — não é uma medida de
              produtividade. {!canSeeAll && 'Você vê apenas as suas próprias métricas.'}
            </span>
          </p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      {peopleMetricsMeta && (
        <div className="text-[11px] text-text-secondary mb-3">
          Período: {formatAbsolute(peopleMetricsMeta.window.from)} – {formatAbsolute(peopleMetricsMeta.window.to)}
          {' · '}Recorte: quadros que você acessa
          {peopleMetricsMeta.self_only && ' · mostrando só as suas métricas'}
        </div>
      )}

      {canSeeAll && (
        <div className="relative mb-3 max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-text-secondary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar pessoa"
            className="w-full bg-card border border-border rounded-md pl-7 pr-3 py-1.5 text-sm text-text-primary outline-none focus:border-accent/50"
          />
        </div>
      )}

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loadingPeopleMetrics ? (
          <div className="text-center py-12 text-sm text-text-secondary">Carregando...</div>
        ) : peopleMetricsError ? (
          <div className="text-center py-12 text-sm text-danger">{peopleMetricsError.message}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-sm text-text-secondary">Sem atividade no período.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-text-secondary">
                <th className="px-4 py-2.5 font-semibold">Pessoa</th>
                <th className="px-4 py-2.5 font-semibold text-right">Iniciados</th>
                <th className="px-4 py-2.5 font-semibold text-right">Concluídos</th>
                <th className="px-4 py-2.5 font-semibold text-right">Cycle time (p50)</th>
                <th className="px-4 py-2.5 font-semibold text-right">Cycle time (p85)</th>
                <th className="px-4 py-2.5 font-semibold text-right">Tempo em espera</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.user_id} className="border-b border-border/50 last:border-0 hover:bg-white/[0.02]">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <Avatar name={p.name} url={p.avatar_url} />
                      <span className="text-text-primary">{p.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-text-primary tabular-nums">{p.started}</td>
                  <td className="px-4 py-2.5 text-right text-text-primary tabular-nums">{p.completed}</td>
                  <td className="px-4 py-2.5 text-right text-text-secondary tabular-nums">
                    {p.cycle_time.n > 0 ? formatDuration(p.cycle_time.p50_ms) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right text-text-secondary tabular-nums">
                    {p.cycle_time.n > 0 ? formatDuration(p.cycle_time.p85_ms) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right text-text-secondary tabular-nums">
                    {formatDuration(p.waiting_ms)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
